import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { Separator } from '../components/ui/separator';
import { Progress } from '../components/ui/progress';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import {
  Sparkles, Send, RefreshCw, Trash2, Database, Users, UtensilsCrossed,
  Building2, GitBranch, AlertTriangle, CheckCircle, Zap, Brain,
  Clock, MapPin, Calendar, ListChecks, Activity, ChevronRight, RotateCcw
} from 'lucide-react';
import api from '../services/api';
import { toast } from 'sonner';
import { format, parseISO } from 'date-fns';

const fmtTime = (d) => { try { return d ? format(parseISO(d), 'MMM d, h:mm a') : 'Never'; } catch { return 'Never'; } };

// ── Action colour map ──
const ACTION_STYLES = {
  mark_followup_complete:  { bg: 'bg-green-50 border-green-200',  text: 'text-green-800',  dot: 'bg-green-500',  label: 'Follow-up logged'     },
  reschedule_outreach:     { bg: 'bg-blue-50 border-blue-200',    text: 'text-blue-800',   dot: 'bg-blue-500',   label: 'Rescheduled'          },
  mark_stage_complete:     { bg: 'bg-teal-50 border-teal-200',    text: 'text-teal-800',   dot: 'bg-teal-500',   label: 'Stage complete'       },
  snooze_followup:         { bg: 'bg-amber-50 border-amber-200',  text: 'text-amber-800',  dot: 'bg-amber-500',  label: 'Snoozed'             },
  update_contact_note:     { bg: 'bg-purple-50 border-purple-200',text: 'text-purple-800', dot: 'bg-purple-500', label: 'Note updated'         },
  advance_outreach:        { bg: 'bg-indigo-50 border-indigo-200',text: 'text-indigo-800', dot: 'bg-indigo-500', label: 'Advanced'             },
  add_contact_to_priority: { bg: 'bg-red-50 border-red-200',      text: 'text-red-800',    dot: 'bg-red-500',    label: 'Prioritised'          },
  bulk_reschedule_overdue: { bg: 'bg-orange-50 border-orange-200',text: 'text-orange-800', dot: 'bg-orange-500', label: 'Bulk rescheduled'     },
};

const DEFAULT_STYLE = { bg: 'bg-muted border-border', text: 'text-foreground', dot: 'bg-muted-foreground', label: 'Action' };

// ── Suggested prompts split into Q&A vs Action ──
const QUERY_PROMPTS = [
  { icon: '🔍', text: 'Who needs follow-up today?',                 label: 'Query' },
  { icon: '📊', text: 'Which restaurants have the highest revenue?', label: 'Query' },
  { icon: '🏖️', text: 'List all contacts in Montauk',               label: 'Query' },
  { icon: '🤝', text: 'Show me all RE Brokers and their status',     label: 'Query' },
];
const ACTION_PROMPTS = [
  { icon: '🗓', text: 'Redistribute all overdue follow-ups over the next 2 weeks', label: 'Action' },
  { icon: '⏭', text: 'Advance the outreach stage for all Day 1 contacts who are overdue', label: 'Action' },
  { icon: '🎯', text: 'Prioritise the top 5 press contacts for immediate follow-up', label: 'Action' },
  { icon: '⏸', text: 'Snooze all non-priority follow-ups by 3 days', label: 'Action' },
  { icon: '📝', text: 'Mark all Day 1 overdue sequences as complete and advance them', label: 'Action' },
  { icon: '📅', text: 'Reschedule overdue outreach for all hotel contacts to next Monday', label: 'Action' },
];

const DATA_SOURCES = [
  { key: 'contact_count',    label: 'Contacts',   icon: Users,          dot: 'bg-blue-500',   text: 'text-blue-600',   bg: 'bg-blue-50 border-blue-200'   },
  { key: 'restaurant_count', label: 'Restaurants', icon: UtensilsCrossed, dot: 'bg-amber-500', text: 'text-amber-600',  bg: 'bg-amber-50 border-amber-200'  },
  { key: 'space_count',      label: 'Spaces',      icon: Building2,      dot: 'bg-teal-500',   text: 'text-teal-600',   bg: 'bg-teal-50 border-teal-200'   },
  { key: 'sequence_count',   label: 'Sequences',   icon: GitBranch,      dot: 'bg-purple-500', text: 'text-purple-600', bg: 'bg-purple-50 border-purple-200' },
];

// ── Markdown renderer ──
function MsgContent({ content }) {
  const lines = content.split('\n');
  return (
    <div className="text-sm leading-relaxed space-y-0.5">
      {lines.map((line, i) => {
        if (line.startsWith('### ')) return <h3 key={i} className="font-bold mt-2 mb-0.5" style={{ fontFamily: 'Cormorant Garamond,serif', fontSize: '1rem' }}>{line.slice(4)}</h3>;
        if (line.startsWith('## '))  return <h2 key={i} className="font-bold mt-3 mb-0.5" style={{ fontFamily: 'Cormorant Garamond,serif', fontSize: '1.1rem' }}>{line.slice(3)}</h2>;
        if (line.startsWith('# '))   return <h1 key={i} className="font-bold mt-3 mb-1"   style={{ fontFamily: 'Cormorant Garamond,serif', fontSize: '1.2rem' }}>{line.slice(2)}</h1>;
        if (/^[•\-\*] /.test(line))  return <div key={i} className="flex gap-2"><span className="text-primary flex-shrink-0">•</span><span>{line.slice(2)}</span></div>;
        if (/^\d+\.\s/.test(line))   return <div key={i} className="flex gap-2"><span className="font-semibold text-primary flex-shrink-0">{line.match(/^\d+/)[0]}.</span><span>{line.replace(/^\d+\.\s/, '')}</span></div>;
        if (line.trim() === '' || line.startsWith('━')) return <div key={i} className="h-1" />;
        return <p key={i}>{line}</p>;
      })}
    </div>
  );
}

// ── Action chips rendered inside message ──
function ActionChips({ actions }) {
  if (!actions?.length) return null;
  return (
    <div className="mt-3 pt-3 border-t border-border space-y-1.5">
      <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground flex items-center gap-1.5">
        <Zap size={11} /> {actions.length} action{actions.length > 1 ? 's' : ''} executed
      </p>
      <div className="flex flex-wrap gap-1.5">
        {actions.map((a, i) => {
          const style = ACTION_STYLES[a.type] || DEFAULT_STYLE;
          return (
            <div key={i} className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${style.bg} ${style.text}`}>
              <span>{a.icon || '•'}</span>
              <span className="font-semibold">{a.contact_name?.split(',')[0]?.split(' ').slice(0, 2).join(' ')}</span>
              <span className="opacity-70">— {a.detail}</span>
              {a.status === 'error' && <AlertTriangle size={10} className="text-red-500" />}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function AIAgent() {
  const [agentStatus, setAgentStatus] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [syncProgress, setSyncProgress] = useState(0);
  const [syncStep, setSyncStep] = useState('');
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(true);
  const [recentActions, setRecentActions] = useState([]);
  const [agentMode, setAgentMode] = useState(true);   // true = agentic mode, false = Q&A only
  const [activeTab, setActiveTab] = useState('chat'); // 'chat' | 'activity'
  const messagesEndRef = useRef(null);

  useEffect(() => { loadStatus(); loadHistory(); loadRecentActions(); }, []);
  useEffect(() => { messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  const loadStatus = async () => {
    try { const r = await api.get('/api/ai/agent/status'); setAgentStatus(r.data); } catch {}
  };

  const loadHistory = async () => {
    setLoadingHistory(true);
    try {
      const r = await api.get('/api/ai/agent/chat/history');
      if (r.data?.length) {
        setMessages(r.data.map(m => ({ role: m.role, content: m.content, executed_actions: m.executed_actions || [] })));
      }
    } catch {} finally { setLoadingHistory(false); }
  };

  const loadRecentActions = async () => {
    try { const r = await api.get('/api/ai/agent/actions/recent'); setRecentActions(r.data || []); } catch {}
  };

  const syncAgent = async () => {
    setSyncing(true);
    setSyncProgress(0);
    const steps = [[15,'Loading contacts…'],[35,'Loading restaurants…'],[55,'Loading sequences…'],[75,'Building knowledge base…'],[90,'Training agent…'],[100,'Ready!']];
    let si = 0;
    const timer = setInterval(() => {
      if (si < steps.length - 1) { setSyncProgress(steps[si][0]); setSyncStep(steps[si][1]); si++; }
    }, 380);
    try {
      const r = await api.post('/api/ai/agent/sync');
      clearInterval(timer);
      setSyncProgress(100); setSyncStep('Ready!');
      setAgentStatus(r.data);
      toast.success('Agent synced!');
      setTimeout(() => { setSyncing(false); setSyncProgress(0); setSyncStep(''); }, 700);
    } catch { clearInterval(timer); setSyncing(false); toast.error('Sync failed'); }
  };

  const sendMessage = async (text) => {
    const msg = (text || input).trim();
    if (!msg || sending) return;
    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: msg, executed_actions: [] }]);
    setSending(true);
    try {
      const endpoint = agentMode ? '/api/ai/agent/execute' : '/api/ai/agent/chat';
      const r = await api.post(endpoint, { message: msg });
      const newMsg = { role: 'assistant', content: r.data.response, executed_actions: r.data.executed_actions || [] };
      setMessages(prev => [...prev, newMsg]);
      if (r.data.actions_count > 0) {
        toast.success(`Agent executed ${r.data.actions_count} action${r.data.actions_count > 1 ? 's' : ''}`);
        loadRecentActions();
        loadStatus();
      }
    } catch {
      toast.error('Failed to get response');
      setMessages(prev => [...prev, { role: 'assistant', content: 'Sorry, I encountered an error.', executed_actions: [] }]);
    } finally { setSending(false); }
  };

  const clearChat = async () => {
    if (!window.confirm('Clear all chat history?')) return;
    try { await api.delete('/api/ai/agent/chat/history'); setMessages([]); toast.success('Chat cleared'); } catch { toast.error('Failed'); }
  };

  const isReady = agentStatus?.synced;
  const totalActionsToday = recentActions.length;

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="page-title flex items-center gap-2"><Brain size={26} className="text-primary" /> AI Intelligence Agent</h1>
          <p className="page-subtitle">Trained on your live CRM — answers questions <strong>and</strong> executes actions autonomously</p>
        </div>
        <div className="flex items-center gap-2">
          {isReady && <span className="flex items-center gap-1.5 text-xs font-medium text-teal-700 bg-teal-50 border border-teal-200 px-3 py-1.5 rounded-full"><span className="w-2 h-2 rounded-full bg-teal-500 animate-pulse" />Active</span>}
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-5">

        {/* ── LEFT PANEL ── */}
        <div className="space-y-4">

          {/* Knowledge base card */}
          <Card className={`overflow-hidden border-2 ${isReady ? 'border-teal-200' : 'border-dashed border-border'}`}>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base flex items-center gap-2" style={{ fontFamily: 'Cormorant Garamond,serif' }}>
                  <Database size={15} className="text-primary" /> Knowledge Base
                </CardTitle>
                {isReady && <CheckCircle size={15} className="text-teal-600" />}
              </div>
              {agentStatus?.last_synced && (
                <p className="text-xs text-muted-foreground flex items-center gap-1"><Clock size={10} />Last synced: {fmtTime(agentStatus.last_synced)}</p>
              )}
            </CardHeader>
            <CardContent className="pt-0 space-y-3">
              <Button onClick={syncAgent} disabled={syncing} className="w-full bg-primary" data-testid="agent-sync-btn">
                {syncing ? <span className="flex items-center gap-2"><div className="w-4 h-4 border-2 border-primary-foreground border-t-transparent rounded-full animate-spin" />{syncStep}</span>
                         : <span className="flex items-center gap-2"><Zap size={14} />{isReady ? 'Re-Sync Agent' : 'Sync Agent with CRM Data'}</span>}
              </Button>
              {syncing && <div className="space-y-1"><Progress value={syncProgress} className="h-1.5" /><p className="text-xs text-center text-muted-foreground">{syncProgress}%</p></div>}

              {isReady && (
                <div className="grid grid-cols-2 gap-2 pt-1">
                  {DATA_SOURCES.map(s => (
                    <div key={s.key} className={`flex flex-col items-center py-2.5 rounded-xl border ${s.bg}`}>
                      <s.icon size={16} className={s.text} />
                      <div className={`text-xl font-bold tabular-nums mt-1 ${s.text}`}>{agentStatus[s.key] || 0}</div>
                      <div className="text-xs text-muted-foreground">{s.label}</div>
                    </div>
                  ))}
                </div>
              )}
              {isReady && agentStatus?.overdue_count > 0 && (
                <div className="flex items-center gap-2 p-2.5 rounded-lg bg-red-50 border border-red-200">
                  <AlertTriangle size={13} className="text-red-600 flex-shrink-0" />
                  <span className="text-xs text-red-700 font-medium">{agentStatus.overdue_count} contacts overdue</span>
                </div>
              )}
              {!isReady && !syncing && <div className="text-center py-4 text-sm text-muted-foreground">Sync to activate full CRM intelligence</div>}
            </CardContent>
          </Card>

          {/* Agent Mode toggle */}
          <Card>
            <CardContent className="py-4">
              <div className="flex items-center justify-between">
                <div>
                  <div className="font-semibold text-sm text-foreground flex items-center gap-2">
                    <Zap size={14} className={agentMode ? 'text-primary' : 'text-muted-foreground'} />
                    Autonomous Mode
                  </div>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {agentMode ? 'Agent can execute CRM actions' : 'Q&A only — no data changes'}
                  </p>
                </div>
                <Switch checked={agentMode} onCheckedChange={setAgentMode} data-testid="agent-mode-switch" />
              </div>
              {agentMode && (
                <div className="mt-3 p-2.5 rounded-lg bg-primary/5 border border-primary/20">
                  <p className="text-xs text-primary font-medium">✦ Can reschedule, complete stages, snooze follow-ups, prioritise contacts & more</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Activity log */}
          <Card>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm flex items-center gap-2"><Activity size={13} className="text-primary" />Recent Agent Actions</CardTitle>
                {totalActionsToday > 0 && <Badge variant="secondary" className="text-xs">{totalActionsToday}</Badge>}
              </div>
            </CardHeader>
            <CardContent className="pt-0">
              {recentActions.length === 0 ? (
                <div className="text-center py-5 text-xs text-muted-foreground">No actions yet — try an action prompt</div>
              ) : (
                <div className="space-y-1.5 max-h-56 overflow-y-auto">
                  {recentActions.map((a, i) => {
                    const style = ACTION_STYLES[a.action?.type] || DEFAULT_STYLE;
                    return (
                      <div key={i} className={`flex items-start gap-2 p-2 rounded-lg border text-xs ${style.bg}`}>
                        <span className="flex-shrink-0 mt-0.5">{a.action?.icon || '•'}</span>
                        <div className="flex-1 min-w-0">
                          <div className={`font-semibold truncate ${style.text}`}>{a.action?.contact_name?.split(',')[0]}</div>
                          <div className="text-muted-foreground truncate">{a.action?.detail}</div>
                          <div className="text-muted-foreground/60 text-xs">{fmtTime(a.timestamp)}</div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Category breakdown */}
          {isReady && agentStatus?.category_breakdown && (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2"><Users size={13} className="text-primary" />Contacts by Category</CardTitle>
              </CardHeader>
              <CardContent className="pt-0 space-y-1.5">
                {Object.entries(agentStatus.category_breakdown).sort(([,a],[,b]) => b-a).map(([cat, count]) => (
                  <div key={cat} className="flex items-center justify-between text-xs">
                    <span className="text-muted-foreground truncate flex-1">{cat}</span>
                    <div className="flex items-center gap-2 ml-2">
                      <div className="w-14 bg-muted rounded-full h-1.5 overflow-hidden">
                        <div className="h-full bg-primary rounded-full" style={{ width: `${Math.min(100,(count/(agentStatus.contact_count||1))*100*4)}%` }} />
                      </div>
                      <span className="font-semibold text-foreground w-5 text-right tabular-nums">{count}</span>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}
        </div>

        {/* ── RIGHT: Chat ── */}
        <div className="xl:col-span-2 flex flex-col" style={{ height: '80vh' }}>
          <Card className="flex flex-col h-full">
            {/* Chat header */}
            <div className="px-4 py-3 border-b border-border flex items-center justify-between flex-shrink-0">
              <div className="flex items-center gap-3">
                <div className="relative">
                  <div className="w-10 h-10 rounded-xl bg-primary flex items-center justify-center">
                    <Brain size={18} className="text-primary-foreground" />
                  </div>
                  {agentMode && <span className="absolute -top-1 -right-1 w-3.5 h-3.5 bg-teal-500 rounded-full border-2 border-card flex items-center justify-center"><Zap size={7} className="text-white" /></span>}
                </div>
                <div>
                  <div className="font-semibold text-sm" style={{ fontFamily: 'Cormorant Garamond,serif' }}>Hamptons XP Intelligence Agent</div>
                  <div className="text-xs text-muted-foreground">
                    {agentMode ? '⚡ Autonomous mode — can read & write CRM data' : '💬 Q&A mode — read only'}
                  </div>
                </div>
              </div>
              <Button variant="ghost" size="icon" className="w-8 h-8 text-muted-foreground" onClick={clearChat}><Trash2 size={14} /></Button>
            </div>

            {/* Messages */}
            <ScrollArea className="flex-1 px-4 py-3">
              {loadingHistory ? (
                <div className="flex justify-center py-8"><div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>
              ) : messages.length === 0 ? (
                <div className="space-y-5 py-4">
                  <div className="text-center">
                    <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-3">
                      <Brain size={28} className="text-primary" />
                    </div>
                    <h3 className="font-semibold" style={{ fontFamily:'Cormorant Garamond,serif', fontSize:'1.3rem' }}>
                      {isReady ? (agentMode ? 'Autonomous Agent Ready' : 'Agent Ready') : 'Sync to Activate'}
                    </h3>
                    <p className="text-sm text-muted-foreground mt-1 max-w-sm mx-auto">
                      {isReady ? `I know all ${agentStatus.contact_count} contacts, ${agentStatus.restaurant_count} restaurants, and every outreach sequence.${agentMode ? ' In Autonomous mode I can directly modify your CRM.' : ''}`
                               : 'Click Sync Agent to train me on your CRM data first.'}
                    </p>
                  </div>

                  {isReady && (
                    <>
                      <div>
                        <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2 flex items-center gap-1.5"><Brain size={11} /> Ask a Question</p>
                        <div className="grid grid-cols-2 gap-1.5">
                          {QUERY_PROMPTS.map((q, i) => (
                            <button key={i} onClick={() => sendMessage(q.text)}
                              className="flex items-start gap-2 p-2.5 rounded-xl border border-border bg-card hover:bg-muted text-left transition-colors">
                              <span className="text-base flex-shrink-0">{q.icon}</span>
                              <span className="text-xs text-foreground">{q.text}</span>
                            </button>
                          ))}
                        </div>
                      </div>
                      {agentMode && (
                        <div>
                          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2 flex items-center gap-1.5"><Zap size={11} className="text-primary" /> Execute an Action</p>
                          <div className="grid grid-cols-1 gap-1.5">
                            {ACTION_PROMPTS.map((q, i) => (
                              <button key={i} onClick={() => sendMessage(q.text)}
                                className="flex items-center gap-2.5 p-2.5 rounded-xl border border-primary/20 bg-primary/5 hover:bg-primary/10 text-left transition-colors group">
                                <span className="text-base flex-shrink-0">{q.icon}</span>
                                <span className="text-xs text-foreground flex-1">{q.text}</span>
                                <ChevronRight size={12} className="text-primary opacity-0 group-hover:opacity-100 flex-shrink-0 transition-opacity" />
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>
              ) : (
                <div className="flex flex-col gap-4">
                  {messages.map((msg, i) => (
                    <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'} fade-in`}>
                      {msg.role === 'assistant' && (
                        <div className="relative w-8 h-8 rounded-xl bg-primary/10 flex items-center justify-center mr-2.5 mt-1 flex-shrink-0">
                          <Brain size={14} className="text-primary" />
                          {msg.executed_actions?.length > 0 && (
                            <span className="absolute -top-1 -right-1 w-4 h-4 bg-teal-500 rounded-full flex items-center justify-center">
                              <Zap size={8} className="text-white" />
                            </span>
                          )}
                        </div>
                      )}
                      <div className={`${msg.role === 'user' ? 'msg-user max-w-[80%]' : 'bg-card border border-border rounded-2xl rounded-tl-sm px-4 py-3 max-w-[90%] shadow-sm'}`}>
                        {msg.role === 'user'
                          ? <span className="text-sm">{msg.content}</span>
                          : <>
                              <MsgContent content={msg.content} />
                              <ActionChips actions={msg.executed_actions} />
                            </>
                        }
                      </div>
                    </div>
                  ))}
                  {sending && (
                    <div className="flex justify-start fade-in">
                      <div className="w-8 h-8 rounded-xl bg-primary/10 flex items-center justify-center mr-2.5 flex-shrink-0">
                        <Brain size={14} className="text-primary" />
                      </div>
                      <div className="bg-card border border-border rounded-2xl rounded-tl-sm px-4 py-3">
                        <div className="flex items-center gap-2 text-xs text-muted-foreground mb-1">
                          {agentMode && <><Zap size={10} className="text-primary animate-pulse" /><span>Agent working…</span></>}
                        </div>
                        <div className="flex items-center gap-1"><span className="typing-dot"/><span className="typing-dot"/><span className="typing-dot"/></div>
                      </div>
                    </div>
                  )}
                  <div ref={messagesEndRef} />
                </div>
              )}
            </ScrollArea>

            {/* Input */}
            <div className="px-4 py-3 border-t border-border flex-shrink-0 space-y-2">
              {!isReady && (
                <div className="flex items-center gap-2 p-2.5 rounded-lg bg-amber-50 border border-amber-200">
                  <AlertTriangle size={12} className="text-amber-600 flex-shrink-0" />
                  <span className="text-xs text-amber-700">Sync the agent first to unlock full CRM knowledge</span>
                  <Button size="sm" className="ml-auto h-6 text-xs bg-amber-600 hover:bg-amber-700" onClick={syncAgent}>Sync Now</Button>
                </div>
              )}
              <div className="flex gap-2">
                <Textarea
                  value={input}
                  onChange={e => setInput(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); } }}
                  placeholder={agentMode ? 'Ask a question or give an instruction — I can update your CRM…' : 'Ask anything about your CRM data…'}
                  className="flex-1 min-h-[44px] max-h-[100px] resize-none text-sm"
                  disabled={sending}
                  data-testid="agent-chat-input"
                  rows={1}
                />
                <Button onClick={() => sendMessage()} disabled={sending || !input.trim()} size="icon" className="h-11 w-11 flex-shrink-0 bg-primary" data-testid="agent-chat-send">
                  <Send size={15} />
                </Button>
              </div>
              <p className="text-xs text-muted-foreground text-center">
                Enter to send · {agentMode ? <span className="text-primary font-medium">⚡ Autonomous mode active — agent can modify your CRM</span> : 'Q&A mode — toggle Autonomous Mode to enable actions'}
              </p>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}
